/**
 * DecisionFlow — RepShell
 * ui/src/shells/RepShell.jsx
 * S73-UI-04 / S804
 *
 * Shell for user_type = "rep". Route root: /rep/
 *
 * OLD-ARCH ROUTES (preserved per Decision 5 — do not remove until new routes verified):
 *   /rep/payout    — MyPayout    (old-arch: payout-summaries EP 12.1/12.2)
 *   /rep/statement — MyStatement
 *   /rep/disputes  — Disputes
 *
 * NEW-ARCH ROUTES (S804 — added alongside old routes):
 *   /rep/my-comp        — MyCompensation    (new-arch: EP 48 dashboard + EP 49/50)
 *   /rep/payout-detail  — RepPayoutExplanation (new-arch: EP 51)
 *   /rep/history        — CompensationHistory  (new-arch: EP 52)
 *
 * Nav shows BOTH sets. New-arch items labeled "(New)".
 * Default route unchanged: /rep/payout (old-arch).
 * Switch to new default after browser verification + owner sign-off (Decision 5).
 *
 * Architecture rules:
 *   DD-04: No cross-shell imports. Shared/ only.
 *   DD-05: Role always from JWT user_type. Never from UI state.
 *   DD-06: Every value from live API. No hardcoded display values in prod.
 *   §1.3: Rep sees own data only. Server enforces position scoping.
 *   AH-RBAC-PAYOUT: payout_summaries_router allows rep own payout_summary_id.
 *
 * New-arch API endpoints (S804):
 *   GET /api/v1/rep/{positionId}/dashboard     — EP 48 (MyCompensation)
 *   GET /api/v1/rep/{positionId}/plan-summary  — EP 49 (RepPlanSummary via MyCompensation)
 *   GET /api/v1/rep/{positionId}/credits       — EP 50 (RepCreditList via MyCompensation)
 *   GET /api/v1/payout-summaries/{id}          — EP 12.2 (RepPayoutExplanation My Payout detail)
 *   GET /api/v1/rep/{positionId}/payout-explanation — EP 51 (RepPayoutExplanation)
 *   GET /api/v1/rep/{positionId}/history       — EP 52 (CompensationHistory)
 *
 * Old-arch API endpoints (unchanged):
 *   GET /api/v1/payout-summaries?is_latest_final=true
 *   GET /api/v1/statement-batches
 *   GET /api/v1/fiscal-periods?period_status=open
 *
 * positionId: resolved from auth user_account.position_id via JWT.
 *   In new-arch, RepShell reads positionId from user context (user.position_id).
 *   Server-side: _verify_own_position confirms position.person_uuid = user.user_id.
 */

import { useState, useEffect } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import {
  FileText, MessageSquareText, X, Download, AlertTriangle,
  LayoutDashboard, Receipt, Clock,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext.jsx";
import Button from "../components/shared/Button.jsx";
import Card from "../components/shared/Card.jsx";
import CopilotPanel from "../components/shared/CopilotPanel.jsx";
import Field from "../components/shared/Field.jsx";
import Pill from "../components/shared/Pill.jsx";
import TopBar from "../components/shared/TopBar.jsx";
import ThemeButton from "../components/shared/ThemeButton.jsx";

// New-arch rep components (S804)
import MyCompensation from "../components/rep/MyCompensation.jsx";
import RepPayoutExplanation from "../components/rep/RepPayoutExplanation.jsx";
import CompensationHistory from "../components/rep/CompensationHistory.jsx";
// S945: unified payout explanation panel — slide-in used across rep/manager/comp-admin
// RepPayoutExplanation kept for /rep/payout-detail full-page route (inline layout)
import PayoutExplanationPanel from "../components/shared/PayoutExplanationPanel.jsx";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Data hooks — each panel owns its fetch (DD-06)
// Cookie auth only — no Bearer token (S160 auth fix)
// ---------------------------------------------------------------------------

function useFiscalPeriods() {
  const [periods, setPeriods] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/fiscal-periods`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        const all = data?.data ?? [];
        const sorted = all
          .slice()
          .sort((a, b) => new Date(b.period_start_date) - new Date(a.period_start_date));
        setPeriods(sorted);
      })
      .catch(() => setError("Periods unavailable"));
  }, []);

  return { periods, error };
}

function usePayoutSummary(fiscalPeriodId) {
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fiscalPeriodId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPayout(null);

    // Step 1 — list endpoint to find payout_summary_id for this period
    fetch(
      `${API_BASE}/payout-summaries?fiscal_period_id=${fiscalPeriodId}&is_latest_final=true`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => {
        const row = data?.data?.[0] ?? null;
        if (!row) { if (!cancelled) { setPayout(null); setLoading(false); } return; }
        // Step 2 — detail endpoint for full shape including ote_reporting, payout_as_pct_ote
        return fetch(
          `${API_BASE}/payout-summaries/${row.payout_summary_id}`,
          { credentials: "include" }
        )
          .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
          .then((detail) => {
            if (cancelled || !detail) return;
            // Normalise detail response shape to match what MyPayout renders.
            // Detail endpoint uses different field names from list endpoint.
            const arrComp = detail.components?.find(c => c.component_type === "ARR");
            const mboComp = detail.components?.find(c => c.component_type === "MBO");
            const trace   = detail.calculation_trace ?? {};
            const normalised = {
              ...detail,
              attainment_pct:   arrComp?.attainment_pct   ?? null,
              ote_reporting:    trace.ote_midpoint         ?? null,
              arr_payout:       arrComp?.resolved_payout   ?? null,
              mbo_payout:       mboComp?.resolved_payout   ?? "0.00",
              payout_as_pct_ote: (
                trace.ote_midpoint && detail.total_payout
                  ? (parseFloat(detail.total_payout) / parseFloat(trace.ote_midpoint)).toFixed(6)
                  : null
              ),
              // Map resolved_payout → component_payout for component breakdown render
              components: detail.components?.map(c => ({
                ...c,
                component_payout: c.resolved_payout,
                quota:  c.quota,
                credit: c.credit,
              })) ?? [],
            };
            setPayout(normalised);
          });
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [fiscalPeriodId]);

  // payout object includes total_payout, attainment_pct, ote_reporting,
  // payout_as_pct_ote, components[], calculation_trace
  return { payout, loading, error };
}

// ---------------------------------------------------------------------------
// useRepPositionId — resolves the rep's own position_id from the server.
//
// Root cause (S804): /api/v1/auth/me does NOT return position_id.
// periods[0] is the most recent period by start_date (e.g. FY2026-M12) which
// may have no payout data. selectedPeriod is batch-defaulted to the most recent
// RELEASED period (e.g. FY2026-M04) — that is where actual payout data lives.
//
// Correct dependency: fiscalPeriodId from selectedPeriod, not periods[0].
// Uses EP 12.1 GET /api/v1/payout-summaries?fiscal_period_id={fpId}&limit=1.
// The list row contains position_id directly. Server RBAC ensures it is always
// the calling rep's own.
//
// Returns: { positionId: string|null, loading: bool }
// ---------------------------------------------------------------------------

function useRepPositionId(fiscalPeriodId) {
  const [positionId, setPositionId] = useState(null);
  const [payoutSummaryId, setPayoutSummaryId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait until selectedPeriod is resolved — same gate used by all other hooks
    if (!fiscalPeriodId) return;

    let cancelled = false;
    setLoading(true);

    // Try is_latest_final=true first (most common case)
    fetch(
      `${API_BASE}/payout-summaries?fiscal_period_id=${fiscalPeriodId}&is_latest_final=true&limit=1`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => {
        if (cancelled) return;
        const row = data?.data?.[0] ?? null;
        if (row?.position_id) {
          setPositionId(String(row.position_id));
          // S805: also capture payoutSummaryId for EP 12.2 (payout-detail page)
          if (row.payout_summary_id) setPayoutSummaryId(String(row.payout_summary_id));
          return;
        }
        // No final payout — try any run for this period
        return fetch(
          `${API_BASE}/payout-summaries?fiscal_period_id=${fiscalPeriodId}&limit=1`,
          { credentials: "include" }
        )
          .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
          .then((data2) => {
            if (cancelled) return;
            const row2 = data2?.data?.[0] ?? null;
            if (row2?.position_id) setPositionId(String(row2.position_id));
            if (row2?.payout_summary_id) setPayoutSummaryId(String(row2.payout_summary_id));
          });
      })
      .catch(() => { if (!cancelled) setPositionId(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [fiscalPeriodId]);

  return { positionId, payoutSummaryId, loading };
}

function useStatementBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/statement-batches`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then((data) => setBatches(data?.data ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return { batches, loading, error };
}

// ---------------------------------------------------------------------------
// LoadingRow — inline skeleton for loading states
// ---------------------------------------------------------------------------

function LoadingRow() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MyPayout panel — with calculation trace + component breakdown (OI-REP-PAYOUT-TRANSPARENCY)
// OLD-ARCH — preserved per Decision 5
// ---------------------------------------------------------------------------

// AcknowledgmentGate — S616 OI-ACKNOWLEDGMENT-PAYOUT-VISIBILITY-GATE
// Fetches GET /api/v1/my/plan-acknowledgment?fiscal_period_id={id}.
// Returns { acknowledged: bool, loading: bool }.
// Used by MyPayout and MyStatement to mask payout figures until acknowledged.
function useMyAcknowledgment(fiscalPeriodId, refreshKey = 0) {
  const [acknowledged, setAcknowledged] = useState(null); // null = loading
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fiscalPeriodId) { setLoading(false); return; }
    setLoading(true);
    fetch(
      `${API_BASE}/my/plan-acknowledgment?fiscal_period_id=${fiscalPeriodId}`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => setAcknowledged(data?.acknowledged === true))
      .catch(() => setAcknowledged(false))
      .finally(() => setLoading(false));
  }, [fiscalPeriodId, refreshKey]);

  return { acknowledged, loading };
}

// useMyStatementAcknowledgment — S657 OI-STATEMENT-ACKNOWLEDGEMENT
// Hits GET /api/v1/my/statement-acknowledgment?fiscal_period_id={id}
// Returns { acknowledged: bool, loading: bool }
function useMyStatementAcknowledgment(fiscalPeriodId, refreshKey = 0) {
  const [acknowledged, setAcknowledged] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fiscalPeriodId) { setLoading(false); return; }
    setLoading(true);
    fetch(
      `${API_BASE}/my/statement-acknowledgment?fiscal_period_id=${fiscalPeriodId}`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => setAcknowledged(data?.acknowledged === true))
      .catch(() => setAcknowledged(false))
      .finally(() => setLoading(false));
  }, [fiscalPeriodId, refreshKey]);

  return { acknowledged, loading };
}

// AcknowledgmentFlow — S616 OI-ACKNOWLEDGMENT-PAYOUT-VISIBILITY-GATE
// Fetches plan terms, shows key plan details, then allows one-click acknowledge.
// On success: parent increments refreshKey → acknowledgment re-fetches → payout reveals.
function AcknowledgmentFlow({ fiscalPeriodId, onAcknowledged }) {
  const [termsData, setTermsData] = useState(null);
  const [termsLoading, setTermsLoading] = useState(true);
  const [termsError, setTermsError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ackError, setAckError] = useState(null);

  useEffect(() => {
    if (!fiscalPeriodId) return;
    fetch(`${API_BASE}/my/plan-terms?fiscal_period_id=${fiscalPeriodId}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setTermsData)
      .catch((e) => setTermsError(e.message))
      .finally(() => setTermsLoading(false));
  }, [fiscalPeriodId]);

  const terms = termsData?.plan_terms ?? null;
  const pc    = terms?.plan_component ?? null;

  async function handleAcknowledge() {
    if (!terms?.plan_assignment_id) return;
    setSubmitting(true);
    setAckError(null);
    try {
      const res = await fetch(`${API_BASE}/my/plan-acknowledgment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan_assignment_id:    terms.plan_assignment_id,
          fiscal_period_id:      fiscalPeriodId,
          acknowledgment_method: "in_app",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "ACK_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      onAcknowledged();
    } catch (e) {
      setAckError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

    // S1052 — download presigned PDF for acknowledged statement
  async function handleDownloadPdf() {
    if (!fiscalPeriodId) return;
    try {
      const res = await fetch(
        `${API_BASE}/my/statement-download?fiscal_period_id=${fiscalPeriodId}`,
        { credentials: "include" },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "DOWNLOAD_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      window.open(body.presigned_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setAckError(e.message);  // reuses existing error state — no new state needed
    }
  }


  if (termsLoading) return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );

  if (termsError || !terms) return (
    <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
      <span className="mt-0.5 shrink-0">⚠</span>
      <span>No active plan assignment found for this period. Contact your comp admin.</span>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Plan summary — show what the rep is signing up for */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4 space-y-3">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          Your Compensation Plan
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 text-tok-sm">
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan</p>
            <p className="font-medium text-[rgb(var(--color-text))]">{terms.plan_code ?? "—"}</p>
          </div>
          {pc?.ote_midpoint && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">OTE</p>
              <p className="font-medium text-[rgb(var(--color-text))]">
                {Number(pc.ote_midpoint).toLocaleString("en-US", { style: "currency", currency: pc.ote_currency_code ?? "USD" })}
              </p>
            </div>
          )}
          {pc?.variable_pct && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Variable</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{`${(Number(pc.variable_pct) * 100).toFixed(0)}%`}</p>
            </div>
          )}
          {pc?.arr_weight && Number(pc.arr_weight) > 0 && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">ARR Weight</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{`${(Number(pc.arr_weight) * 100).toFixed(0)}%`}</p>
            </div>
          )}
          {pc?.mbo_weight && Number(pc.mbo_weight) > 0 && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">MBO Weight</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{`${(Number(pc.mbo_weight) * 100).toFixed(0)}%`}</p>
            </div>
          )}
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective</p>
            <p className="font-medium text-[rgb(var(--color-text))]">{terms.effective_date ?? "—"}</p>
          </div>
        </div>
      </div>

      {/* Acknowledge CTA */}
      <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3">
        <span className="mt-0.5 shrink-0 text-[rgb(var(--color-warning))]">⚠</span>
        <div className="flex-1 space-y-2">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
            Acknowledge your plan to view your earnings.
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            By acknowledging, you confirm you have reviewed and understood your compensation plan for this period.
          </p>
          {ackError && (
            <p className="text-tok-xs text-[rgb(var(--color-danger))]">{ackError}</p>
          )}
          <button
            onClick={handleAcknowledge}
            disabled={submitting}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Acknowledging…" : "Acknowledge My Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// StatementAcknowledgmentBanner — S657 OI-STATEMENT-ACKNOWLEDGEMENT
// Shown in MyStatement when batch is released and rep has not yet acknowledged.
// Fetches GET /my/statement-summary for payout figures, then POST /my/statement-acknowledgment.
// Mirrors AcknowledgmentFlow pattern (plan ack gate).
function StatementAcknowledgmentBanner({ fiscalPeriodId, statementBatchId, onAcknowledged }) {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ackError, setAckError] = useState(null);

  useEffect(() => {
    if (!fiscalPeriodId) return;
    setSummaryLoading(true);
    fetch(
      `${API_BASE}/my/statement-summary?fiscal_period_id=${fiscalPeriodId}`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [fiscalPeriodId]);

  const fmt = (val) =>
    val != null
      ? Number(val).toLocaleString("en-US", { style: "currency", currency: summary?.payout_currency_code ?? "USD" })
      : "—";

  async function handleAcknowledge() {
    if (!statementBatchId) return;
    setSubmitting(true);
    setAckError(null);
    try {
      const res = await fetch(`${API_BASE}/my/statement-acknowledgment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          fiscal_period_id:      fiscalPeriodId,
          statement_batch_id:    statementBatchId,
          acknowledgment_method: "in_app",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "ACK_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      onAcknowledged();
    } catch (e) {
      setAckError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Payout summary — what the rep is acknowledging */}
      {summaryLoading && <LoadingRow />}
      {!summaryLoading && summary && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4 space-y-3">
          <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
            Your Payout Statement
          </p>
          <div className="grid gap-2 sm:grid-cols-2 text-tok-sm">
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Total Payout</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{fmt(summary.total_payout_amount)}</p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Currency</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{summary.payout_currency_code ?? "—"}</p>
            </div>
            {summary.components?.map((c, i) => (
              <div key={i}>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{c.component_label}</p>
                <p className="font-medium text-[rgb(var(--color-text))]">{fmt(c.component_amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Acknowledge CTA — mirrors AcknowledgmentFlow pattern */}
      <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3">
        <span className="mt-0.5 shrink-0 text-[rgb(var(--color-warning))]">⚠</span>
        <div className="flex-1 space-y-2">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
            Acknowledge your statement to confirm receipt.
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            By acknowledging, you confirm you have reviewed your payout statement for this period.
            Once acknowledged, the dispute window will close.
          </p>
          {ackError && (
            <p className="text-tok-xs text-[rgb(var(--color-danger))]">{ackError}</p>
          )}
          <button
            onClick={handleAcknowledge}
            disabled={submitting || !statementBatchId}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Acknowledging…" : "Acknowledge My Statement"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MyPayout({ period }) {
  const fpId = period?.fiscal_period_id;
  const { payout, loading, error } = usePayoutSummary(fpId ?? null);
  const [ackRefreshKey, setAckRefreshKey] = useState(0);
  const { acknowledged, loading: ackLoading } = useMyAcknowledgment(fpId ?? null, ackRefreshKey);

  const fmt = (val) =>
    val != null
      ? Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" })
      : "—";

  const fmtPct = (val) =>
    val != null ? `${(Number(val) * 100).toFixed(1)}%` : "—";

  const components = payout?.components ?? [];
  const trace = payout?.calculation_trace ?? null;

  // Derive zero-payout reason from available data
  function zeroReason() {
    if (!payout || Number(payout.total_payout) !== 0) return null;
    const attPct = Number(payout.attainment_pct ?? 0);
    if (trace?.rate_band_applied === false || trace?.rate_band_applied === null) {
      return "No rate band applied — attainment may be below the minimum threshold for this plan.";
    }
    if (attPct < 0.5) {
      return `Attainment of ${fmtPct(payout.attainment_pct)} is below the minimum payout threshold for this plan.`;
    }
    if (components.length > 0 && components.every((c) => Number(c.component_payout ?? 0) === 0)) {
      return "All components calculated to $0.00. Check quota, credit, and rate table configuration with your comp admin.";
    }
    return "Payout calculated as $0.00. Contact your comp admin for details.";
  }

  const reason = zeroReason();

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <Card
        title="My Payout"
        action={<Pill tone={payout ? "Complete" : "Draft"}>{payout ? "Final" : "Pending"}</Pill>}
      >
        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load payout data." />}
        {!loading && !error && !payout && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No finalised payout available for this period.</p>
        )}
        {fpId && !loading && !ackLoading && !acknowledged && (
          <AcknowledgmentFlow fiscalPeriodId={fpId} onAcknowledged={() => setAckRefreshKey((k) => k + 1)} />
        )}
        {payout && acknowledged && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Total Payout" value={fmt(payout.total_payout)} />
            <Field label="Attainment" value={fmtPct(payout.attainment_pct)} />
            <Field label="OTE" value={fmt(payout.ote_reporting)} />
            <Field label="Payout as % OTE" value={fmtPct(payout.payout_as_pct_ote)} />
            <Field label="ARR Payout" value={fmt(payout.arr_payout)} />
            <Field label="MBO Payout" value={fmt(payout.mbo_payout)} />
            {Number(payout.nps_payout) > 0 && (
              <Field label="NPS Payout" value={fmt(payout.nps_payout)} />
            )}
            {Number(payout.adoption_payout) > 0 && (
              <Field label="Adoption Payout" value={fmt(payout.adoption_payout)} />
            )}
          </div>
        )}

        {/* Zero payout explanation — only after acknowledged */}
        {reason && acknowledged && (
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-warning))] px-3 py-2.5 text-tok-sm text-[rgb(var(--color-warning))]">
            <span className="mt-0.5 shrink-0">⚠</span>
            <span>{reason}</span>
          </div>
        )}
      </Card>

      {/* Component breakdown — only after acknowledged */}
      {components.length > 0 && acknowledged && (
        <Card title="Component Breakdown">
          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2 px-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
              <span>Component</span>
              <span className="text-right">Attainment</span>
              <span className="text-right">Quota / Credit</span>
              <span className="text-right">Payout</span>
            </div>
            {components.map((c, i) => (
              <div
                key={c.payout_detail_id ?? i}
                className="grid grid-cols-4 gap-2 rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5 text-tok-sm"
              >
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-[rgb(var(--color-text))]">
                    {c.component_type}
                  </span>
                  {c.ramp_factor && Number(c.ramp_factor) < 1 && (
                    <Pill tone="Draft">Ramped {fmtPct(c.ramp_factor)}</Pill>
                  )}
                </div>
                <span className="text-right text-[rgb(var(--color-text-muted))]">
                  {fmtPct(c.attainment_pct)}
                </span>
                <span className="text-right text-[rgb(var(--color-text-muted))]">
                  {fmt(c.quota)} / {fmt(c.credit)}
                </span>
                {/* §15.2 S578: Held badge when gate_met=FALSE (exception blocking gate fired) */}
                {c.gate_met === false && c.source_ref_id?.startsWith("blocked_by_exception:") ? (
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="font-medium text-[rgb(var(--color-text))]">$0.00</span>
                    <Pill tone="Warning">
                      <Link
                        to={`/exceptions/${c.source_ref_id.replace("blocked_by_exception:", "")}`}
                        className="flex items-center gap-1"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Held
                      </Link>
                    </Pill>
                  </div>
                ) : (
                  <span className="text-right font-medium text-[rgb(var(--color-text))]">
                    {fmt(c.component_payout)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Calculation trace */}
      {/* Calculation Detail — only after acknowledged */}
      {trace && acknowledged && (
        <Card title="Calculation Detail">

          {/* ── Proof steps ─────────────────────────────────────────────────
              Grouped by section. Each section gets a header and its steps.
              value_type drives display formatting:
                currency → value already pre-formatted by router as $X,XXX.XX
                percent  → already formatted as XX.XX%
                raw      → display as-is
              is_negative → danger colour (clawback / recovery)
              is_zero     → muted warning colour (zero payout diagnostic)
              is_result   → highlighted row (section subtotal)
              is_total    → bold highlighted row (grand total)
          ─────────────────────────────────────────────────────────────────── */}
          {Array.isArray(trace.proof_steps) && trace.proof_steps.length > 0 ? (() => {
            // Group steps by section preserving insertion order
            const sections = [];
            const seen = new Map();
            for (const step of trace.proof_steps) {
              const sec = step.section || "Other";
              if (!seen.has(sec)) {
                seen.set(sec, []);
                sections.push({ section: sec, steps: seen.get(sec) });
              }
              seen.get(sec).push(step);
            }

            return (
              <div className="space-y-4">
                {sections.map(({ section, steps }) => (
                  <div key={section}>
                    {/* Section header — hidden for TOTAL (it renders as a bold step) */}
                    {section !== "TOTAL" && section !== "Status" && (
                      <p className="mb-1 text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] opacity-80">
                        {section}
                      </p>
                    )}
                    <div className="space-y-0.5">
                      {steps.map((step, i) => {
                        const isNeg   = step.is_negative;
                        const isZero  = step.is_zero;
                        const isRes   = step.is_result;
                        const isTotal = step.is_total;

                        const rowCls = [
                          "flex items-baseline justify-between gap-3 rounded-lg px-3 py-2 text-tok-sm",
                          isTotal
                            ? "bg-[rgb(var(--color-primary))] text-white"
                            : isRes
                            ? "bg-[rgb(var(--color-primary)/0.08)] text-[rgb(var(--color-primary))]"
                            : "text-[rgb(var(--color-text-muted))]",
                        ].join(" ");

                        const valCls = [
                          "shrink-0 tabular-nums font-mono",
                          isNeg
                            ? "text-[rgb(var(--color-danger))]"
                            : isZero && isRes
                            ? "text-[rgb(var(--color-warning))]"
                            : isTotal || isRes
                            ? "text-[rgb(var(--color-text))]"
                            : "text-[rgb(var(--color-text-muted))]",
                        ].join(" ");

                        return (
                          <div key={i} className={rowCls}>
                            <span className="flex flex-wrap items-baseline gap-x-2 min-w-0">
                              <span>{step.label}</span>
                              {step.note && (
                                <span className="text-tok-xs text-[rgb(var(--color-text-muted))] font-normal opacity-75">
                                  {step.note}
                                </span>
                              )}
                            </span>
                            <span className={valCls}>{step.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })() : (
            /* Fallback summary fields when proof_steps unavailable (old runs) */
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {trace.ote_midpoint != null && (
                <Field label="OTE Midpoint" value={fmt(trace.ote_midpoint)} />
              )}
              {trace.variable_pct != null && (
                <Field
                  label="Variable %"
                  value={`${(Number(trace.variable_pct) * 100).toFixed(1)}%`}
                />
              )}
              {trace.rate_band_applied != null && (() => {
                const band     = trace.rate_band_applied;
                const hasBand  = !!band;
                const hasRange = hasBand && band.from_pct != null && band.to_pct != null;
                const bandLabel = !hasBand
                  ? "No"
                  : hasRange
                  ? `${(Number(band.from_pct) * 100).toFixed(0)}% – ${(Number(band.to_pct) * 100).toFixed(0)}%`
                  : "Yes";
                return <Field label="Rate Band" value={bandLabel} />;
              })()}
              {trace.interpolation != null && (
                <Field label="Interpolation" value={trace.interpolation ? "Yes" : "No"} />
              )}
              {trace.manual_proof && (
                <div className="col-span-full mt-2 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                  <p className="mb-1 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                    Calculation Proof
                  </p>
                  <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))] break-all">
                    {trace.manual_proof}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MyStatement panel
// ---------------------------------------------------------------------------

// S657 OI-STATEMENT-ACKNOWLEDGEMENT — statement ack gate added.
// When batch released and rep has not acknowledged: show StatementAcknowledgmentBanner.
// After ack: banner dismisses; payout summary visible; dispute window closed server-side.
function MyStatement({ period, batches, batchesLoading, batchesError }) {
  const loading = batchesLoading;
  const error = batchesError;

  // Rep sees released batches only — server-enforced.
  const latest = batches.find(b => b.fiscal_period_id === period?.fiscal_period_id) ?? null;

  // Statement ack gate — S657 OI-STATEMENT-ACKNOWLEDGEMENT
  const [stmtAckRefreshKey, setStmtAckRefreshKey] = useState(0);
  // S1053 — PDF download error state
  const [downloadError, setDownloadError] = useState(null);

  // S1053 — On-demand PDF download: generate on first click, serve presigned URL
  async function handleDownloadPdf() {
    if (!period?.fiscal_period_id) return;
    setDownloadError(null);
    try {
      const res = await fetch(
        `${API_BASE}/rep/my/statement-download?fiscal_period_id=${period.fiscal_period_id}`,
        { credentials: "include" },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "DOWNLOAD_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      window.open(body.presigned_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setDownloadError(e.message);
    }
  }
  const { acknowledged: stmtAcknowledged, loading: stmtAckLoading } =
    useMyStatementAcknowledgment(period?.fiscal_period_id ?? null, stmtAckRefreshKey);

  // Rep's own payout from payout-summaries — scoped server-side by position_id.
  const { payout } = usePayoutSummary(latest?.fiscal_period_id ?? period?.fiscal_period_id ?? null);

  // S1051 local-currency logic: prefer local when present and differs from reporting
  const displayCurrencyCode =
    payout?.local_currency_code && payout.local_currency_code !== payout?.payout_currency_code
      ? payout.local_currency_code
      : payout?.payout_currency_code ?? null;
  const displayPayoutAmount =
    payout?.local_currency_code && payout.local_currency_code !== payout?.payout_currency_code
      ? payout.total_payout_local
      : payout?.total_payout ?? null;

  const fmt = (val) =>
    val != null && displayCurrencyCode
      ? Number(val).toLocaleString("en-US", { style: "currency", currency: displayCurrencyCode })
      : val != null ? String(val) : "—";

  return (
    <div className="space-y-4">
      <Card title="My Statement">
        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load statement data." />}
        {!loading && !error && !latest && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No released statement available for this period.
          </p>
        )}
        {latest && (
          <>
            {/* Statement ack banner — shown until rep acknowledges */}
            {!stmtAckLoading && !stmtAcknowledged && (
              <div className="mb-4">
                <StatementAcknowledgmentBanner
                  fiscalPeriodId={period?.fiscal_period_id ?? null}
                  statementBatchId={latest?.statement_batch_id ?? null}
                  onAcknowledged={() => setStmtAckRefreshKey((k) => k + 1)}
                />
              </div>
            )}
            {/* Statement detail — always visible once batch is released */}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Period" value={period?.period_label ?? period?.period_code ?? "—"} />
              <Field label="Status" value="Released" />
              <Field label="Total Payout" value={
                stmtAckLoading ? "—"
                : !stmtAcknowledged ? "Acknowledge statement to view"
                : payout ? fmt(displayPayoutAmount) : "—"
              } />
              <Field label="Currency" value={displayCurrencyCode ?? latest.payout_currency_code ?? "—"} />
            </div>
            <div className="mt-4">
              {/* S1053 — PDF download enabled post-acknowledgment; generated on-demand */}
              <Button variant="secondary" disabled={!stmtAcknowledged} onClick={handleDownloadPdf}>
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
              {downloadError && (
                <p className="mt-2 text-tok-xs text-[rgb(var(--color-danger))]">{downloadError}</p>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Disputes panel — S805: fully functional (POST /api/v1/disputes, GET list)
// Rep can submit own-position disputes. Router enforces position scoping.
// dispute_type options: core types relevant to rep persona.
// Modifier types (kicker_dispute etc.) require plan linkage fields — excluded
// from rep self-service form (comp_admin handles modifier disputes directly).
// ---------------------------------------------------------------------------

const REP_DISPUTE_TYPES = [
  { value: "attainment_dispute",  label: "Attainment / Quota Dispute" },
  { value: "credit_dispute",      label: "Credit Not Counted" },
  { value: "payment_dispute",     label: "Payment Amount Incorrect" },
  { value: "rate_dispute",        label: "Rate / Band Applied Incorrectly" },
  { value: "system_error",        label: "System / Calculation Error" },
  { value: "other",               label: "Other" },
];

const DISPUTE_STATUS_LABELS = {
  submitted:    { label: "Submitted",    color: "rgb(var(--color-primary))" },
  under_review: { label: "Under Review", color: "rgb(var(--color-warning))" },
  escalated:    { label: "Escalated",    color: "rgb(var(--color-danger))" },
  resolved:     { label: "Resolved",     color: "rgb(var(--color-success))" },
  closed:       { label: "Closed",       color: "rgb(var(--color-text-muted))" },
};

function DisputeStatusPill({ status }) {
  const meta = DISPUTE_STATUS_LABELS[status] ?? { label: status, color: "rgb(var(--color-text-muted))" };
  return (
    <span style={{
      display: "inline-flex", padding: "2px 9px",
      border: `1px solid ${meta.color}40`,
      borderRadius: 999, fontSize: 11, fontWeight: 600,
      color: meta.color, background: `${meta.color}10`,
      whiteSpace: "nowrap",
    }}>
      {meta.label}
    </span>
  );
}

function Disputes({ period, repPositionId }) {
  const fpId = period?.fiscal_period_id;

  // ── Statement acknowledgment gate ──
  // Once a rep acknowledges their statement, the dispute window closes.
  // Reuse existing useMyStatementAcknowledgment hook — same endpoint as MyStatement.
  const { acknowledged: stmtAcknowledged, loading: ackLoading } =
    useMyStatementAcknowledgment(fpId ?? null);

  const disputeWindowClosed = stmtAcknowledged === true;

  // ── Form state ──
  const [modalOpen,    setModalOpen]    = useState(false);
  const [disputeType,  setDisputeType]  = useState(REP_DISPUTE_TYPES[0].value);
  const [description,  setDescription]  = useState("");
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState(null);
  const [submitted,    setSubmitted]    = useState(null); // success result

  // ── Existing disputes list ──
  const [disputes,     setDisputes]     = useState([]);
  const [listLoading,  setListLoading]  = useState(false);
  const [listError,    setListError]    = useState(null);
  const [refreshKey,   setRefreshKey]   = useState(0);

  // Fetch existing disputes for this period
  useEffect(() => {
    if (!fpId) return;
    setListLoading(true);
    setListError(null);
    const params = new URLSearchParams({ fiscal_period_id: fpId, limit: 50 });
    fetch(`${API_BASE}/disputes?${params}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => setDisputes(data?.disputes ?? []))
      .catch((e) => setListError(e.message))
      .finally(() => setListLoading(false));
  }, [fpId, refreshKey]);

  function openModal() {
    if (disputeWindowClosed) return; // should not be reachable — button hidden when closed
    setDisputeType(REP_DISPUTE_TYPES[0].value);
    setDescription("");
    setSubmitError(null);
    setSubmitted(null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setSubmitted(null);
    setSubmitError(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!description.trim()) { setSubmitError("Please describe your dispute."); return; }
    if (!repPositionId)       { setSubmitError("Your position could not be resolved. Try again or contact your admin."); return; }
    if (!fpId)                { setSubmitError("No period selected."); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/disputes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          position_id:         repPositionId,
          fiscal_period_id:    fpId,
          dispute_type:        disputeType,
          dispute_description: description.trim(),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = body?.detail?.error?.message ?? `HTTP ${res.status}`;
        throw new Error(msg);
      }
      setSubmitted(body);
      setRefreshKey((k) => k + 1); // refresh list
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <div className="space-y-4">
      {/* ── Header card ── */}
      <Card title="Disputes">
        {ackLoading ? (
          <div className="animate-pulse h-4 w-2/3 rounded skeleton" />
        ) : disputeWindowClosed ? (
          /* Statement acknowledged → dispute window closed */
          <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
            <span className="mt-0.5 shrink-0 text-[rgb(var(--color-text-muted))]">🔒</span>
            <div>
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                Dispute window closed
              </p>
              <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                You have acknowledged your statement for {period?.period_label ?? "this period"}.
                Once a statement is acknowledged, the dispute window closes and no new disputes
                can be raised for that period. Contact your comp admin if you believe an error
                occurred after acknowledgment.
              </p>
            </div>
          </div>
        ) : (
          /* Window open → allow submission */
          <>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              If you believe your payout or statement contains an error, you can raise a dispute directly below.
              Your comp admin will review and respond.
            </p>
            <div className="mt-4">
              <Button onClick={openModal}>
                <MessageSquareText className="h-4 w-4" />
                Raise Dispute
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* ── Existing disputes for this period ── */}
      <Card title={`Your Disputes${period?.period_label ? ` — ${period.period_label}` : ""}`}>
        {listLoading && (
          <div className="animate-pulse space-y-2">
            <div className="h-4 w-2/3 rounded skeleton" />
            <div className="h-4 w-1/2 rounded skeleton" />
          </div>
        )}
        {listError && (
          <p className="text-tok-sm text-[rgb(var(--color-danger))]">Unable to load disputes.</p>
        )}
        {!listLoading && !listError && disputes.length === 0 && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No disputes raised for this period.
          </p>
        )}
        {!listLoading && disputes.length > 0 && (
          <div className="divide-y divide-[rgb(var(--color-border))]">
            {disputes.map((d) => (
              <div key={d.dispute_id} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                    {REP_DISPUTE_TYPES.find((t) => t.value === d.dispute_type)?.label ?? d.dispute_type}
                  </p>
                  <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))] line-clamp-2">
                    {d.dispute_description}
                  </p>
                  <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                    Submitted {formatDate(d.submitted_at)}
                    {d.resolution_notes ? ` · ${d.resolution_notes}` : ""}
                  </p>
                </div>
                <div className="shrink-0">
                  <DisputeStatusPill status={d.dispute_status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Submit dispute modal ── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-[rgb(var(--color-surface))] p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-tok-base font-bold text-[rgb(var(--color-text))]">
                  Raise a Dispute
                </h3>
                {period?.period_label && (
                  <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                    Period: {period.period_label}
                  </p>
                )}
              </div>
              <button
                onClick={closeModal}
                className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* ── Success state ── */}
            {submitted ? (
              <div className="mt-4 space-y-3">
                <div className="rounded-xl border border-[rgb(var(--color-success)/0.3)] bg-[rgb(var(--color-success)/0.06)] px-4 py-4">
                  <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                    Dispute submitted successfully
                  </p>
                  <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                    Your dispute has been received and is under review.
                  </p>
                  <p className="mt-2 text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">
                    ID: {submitted.dispute_id}
                  </p>
                </div>
                <div className="flex justify-end">
                  <Button onClick={closeModal}>Done</Button>
                </div>
              </div>
            ) : (
              /* ── Form ── */
              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div className="space-y-1">
                  <label className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                    Dispute Type
                  </label>
                  <select
                    value={disputeType}
                    onChange={(e) => setDisputeType(e.target.value)}
                    className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary)/0.4)]"
                  >
                    {REP_DISPUTE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                    Description <span className="text-[rgb(var(--color-danger))]">*</span>
                  </label>
                  <textarea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the issue clearly — include relevant deal names, amounts, or dates..."
                    className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary)/0.4)] resize-none"
                  />
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                    {description.length} / 2000
                  </p>
                </div>

                {submitError && (
                  <p className="rounded-lg bg-[rgb(var(--color-danger)/0.06)] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                    {submitError}
                  </p>
                )}

                <div className="flex items-center justify-end gap-3 pt-1">
                  <Button variant="secondary" type="button" onClick={closeModal} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting || !description.trim()}>
                    {submitting ? "Submitting…" : "Submit Dispute"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// RepCopilotCard removed S241 — replaced by shared CopilotPanel.

// ---------------------------------------------------------------------------
// NEW-ARCH: MyCompensationPage — wraps MyCompensation component
// Route: /rep/my-comp  (Decision 5 — new alongside old)
// positionId resolved via useRepPositionId hook at shell level (S804 fix:
//   /api/v1/auth/me does not return position_id — resolved from payout-summaries).
// ---------------------------------------------------------------------------

function MyCompensationPage({ period, repPositionId, payoutSummaryId, positionLoading, userName }) {
  const navigate = useNavigate();
  const [explanationOpen, setExplanationOpen] = useState(false);

  if (positionLoading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-4 w-2/3 rounded skeleton" />
        <div className="h-4 w-1/2 rounded skeleton" />
      </div>
    );
  }

  if (!repPositionId) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-warning)/0.3)] bg-[rgb(var(--color-warning)/0.06)] px-4 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
        No payout data found for this period. Try selecting a different period, or contact your comp admin.
      </div>
    );
  }

  const fpId = period?.fiscal_period_id;
  if (!fpId) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
        Loading period...
      </div>
    );
  }

  return (
    <>
      <MyCompensation
        positionId={repPositionId}
        fiscalPeriodId={fpId}
        onViewPayoutExplanation={
          payoutSummaryId
            ? () => setExplanationOpen(true)
            : () => navigate("/rep/payout-detail")  // fallback for periods without summary id
        }
        onViewHistory={() => navigate("/rep/history")}
      />
      {/* S945: unified PayoutExplanationPanel — replaces navigate to /rep/payout-detail.
          Opens inline on "View full payout explanation" button click.
          Falls back to navigate() when payoutSummaryId not yet resolved. */}
      {explanationOpen && payoutSummaryId ? (
        <PayoutExplanationPanel
          payoutSummaryId={payoutSummaryId}
          positionName={userName ?? null}
          periodLabel={period?.period_label ?? period?.period_code}
          onClose={() => setExplanationOpen(false)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// NEW-ARCH: RepPayoutExplanationPage — wraps RepPayoutExplanation full-page
// Route: /rep/payout-detail  (Decision 5 — new alongside old)
// S805 REWRITE: Uses EP 12.2 (payout-summaries/{id}) — NOT EP 51.
// S945 NOTE: RepPayoutExplanation kept here for full-page layout.
// PayoutExplanationPanel is the slide-in used everywhere else (my-comp, history,
// comp-admin, manager). Full-page route needs inline layout — not fixed overlay.
// ---------------------------------------------------------------------------

function RepPayoutExplanationPage({ period, repPositionId, payoutSummaryId, positionLoading, userName }) {
  if (positionLoading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-4 w-2/3 rounded skeleton" />
        <div className="h-4 w-1/2 rounded skeleton" />
      </div>
    );
  }

  if (!repPositionId) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-warning)/0.3)] bg-[rgb(var(--color-warning)/0.06)] px-4 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
        No payout data found for this period. Try selecting a different period, or contact your comp admin.
      </div>
    );
  }

  // S1047: Wire to PayoutExplanationPanel inline mode — consistent with all other surfaces.
  // payoutSummaryId resolved at shell level from EP 12.1 — passed directly to EP 12.2 inside PEP.
  return (
    <PayoutExplanationPanel
      inline={true}
      payoutSummaryId={payoutSummaryId ?? null}
      positionName={userName ?? null}
      roleCode={null}
      periodLabel={period?.period_label ?? period?.period_code ?? null}
      viewerRole="rep"
    />
  );
}

// ---------------------------------------------------------------------------
// NEW-ARCH: CompensationHistoryPage — wraps CompensationHistory component
// Route: /rep/history  (Decision 5 — new alongside old)
// positionId resolved at shell level via useRepPositionId.
// ---------------------------------------------------------------------------

function CompensationHistoryPage({ repPositionId, positionLoading }) {
  const navigate = useNavigate();

  if (positionLoading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-4 w-2/3 rounded skeleton" />
        <div className="h-4 w-1/2 rounded skeleton" />
      </div>
    );
  }

  if (!repPositionId) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-warning)/0.3)] bg-[rgb(var(--color-warning)/0.06)] px-4 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
        No payout data found for this period. Try selecting a different period, or contact your comp admin.
      </div>
    );
  }

  // CompensationHistory manages its own right panel internally (S804).
  // No onViewCalculation prop needed — right panel opens inline within the component.
  return (
    <CompensationHistory
      positionId={repPositionId}
    />
  );
}

// ---------------------------------------------------------------------------
// Rep ContextHeader — with period selector (OI-PERIOD-SCOPING)
// ---------------------------------------------------------------------------

function RepContextHeader({ pageTitle, periods, selectedPeriod, onPeriodChange }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">
        {pageTitle}
      </h1>
      {periods && periods.length > 0 && (
        <select
          value={selectedPeriod?.fiscal_period_id ?? ""}
          onChange={(e) => {
            const p = periods.find((x) => String(x.fiscal_period_id) === e.target.value);
            if (p) onPeriodChange(p);
          }}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
        >
          {periods.map((p) => (
            <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
              {p.period_label ?? p.period_code}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rep Left Nav
// Old-arch items retained. New-arch items added with (New) label per Decision 5.
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  // S805: Unified nav — old/new split removed. My Payout superseded by Payout Detail.
  // Statement and Disputes moved from old-arch section into single rep nav.
  // Default route switched to /rep/my-comp (Decision 5 verification complete S805).
  { to: "/rep/my-comp",       label: "Compensation",  icon: LayoutDashboard },
  { to: "/rep/payout-detail", label: "Payout Detail",  icon: Receipt },
  { to: "/rep/history",       label: "History",         icon: Clock },
  { to: "/rep/statement",     label: "My Statement",    icon: FileText },
  { to: "/rep/disputes",      label: "Disputes",        icon: MessageSquareText },
];

function RepNav({ mobileOpen, onClose }) {
  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-52 overflow-y-auto border-r border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pt-16 lg:static lg:pt-0 transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <nav className="p-3 space-y-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-tok-sm font-medium transition ${
                  isActive
                    ? "bg-[rgb(var(--color-primary))] text-white"
                    : "text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page title resolver — covers both old-arch and new-arch routes
// ---------------------------------------------------------------------------

const PAGE_TITLES = {
  "/rep/payout":        "My Payout",
  "/rep/statement":     "My Statement",
  "/rep/disputes":      "Disputes",
  "/rep/my-comp":       "My Compensation",
  "/rep/payout-detail": "Payout Detail",
  "/rep/history":       "History",
};

// ---------------------------------------------------------------------------
// RepShell root
// ---------------------------------------------------------------------------

export default function RepShell() {
  const { user } = useAuth();
  // M040 C0: dark/setDark state and useEffect removed — ThemeButton is self-contained.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(true);

  // Period selector — 5 most recent periods (OI-PERIOD-SCOPING)
  const { periods } = useFiscalPeriods();
  const { batches, loading: batchesLoading, error: batchesError } = useStatementBatches();

  // OI-S1043-PERIOD-DEFAULT-REP (S1049): bootstrap profile fetch to resolve
  // latestDataPeriodId — the most recent fiscal period with finalised payout data.
  // Mirrors ManagerShell bootstrap pattern. Do NOT revert to batch-driven default.
  const [bootstrapProfileData, setBootstrapProfileData] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/rep/profile`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => setBootstrapProfileData(data ?? {}))
      .catch(() => setBootstrapProfileData({}));
  }, []);

  // S804: resolve rep's positionId from payout-summaries (auth/me does not return position_id)
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  // S804: position resolution depends on selectedPeriod — must be declared first
  // S805: useRepPositionId also returns payoutSummaryId for EP 12.2 (payout-detail page)
  const { positionId: repPositionId, payoutSummaryId: repPayoutSummaryId, loading: positionLoading } = useRepPositionId(selectedPeriod?.fiscal_period_id);
  useEffect(() => {
    if (selectedPeriod !== null || periods.length === 0) return;
    // OI-S1043-PERIOD-DEFAULT-REP (S1049): wait for bootstrap profile before defaulting period.
    // bootstrapProfileData===null means fetch not yet complete; {}  means fetch done, no data.
    if (bootstrapProfileData === null) return;
    const latestId = bootstrapProfileData?.latestDataPeriodId ?? null;
    const defaultPeriod = latestId
      ? (periods.find((p) => String(p.fiscal_period_id) === String(latestId)) ?? periods[0])
      : periods[0];
    setSelectedPeriod(defaultPeriod);
  }, [periods, bootstrapProfileData]);

  // S77 — tenant-context: proactive tier gate (Stage 10). ER-1 compliant.
  const [tenantContext, setTenantContext] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/tenant-context`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then(setTenantContext)
      .catch(() => {});
  }, []);
  const aiQueryEnabled = tenantContext?.features?.allows_ai_query ?? null;

  const { pathname } = useLocation();
  const pageTitle = PAGE_TITLES[pathname] ?? "My Payout";

  return (
    <div className="min-h-screen bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))]">
      <TopBar
        userName={user?.full_name ?? user?.email ?? null}
        userRole={user?.user_type ?? "rep"}
        userRoleLabel="Rep"
        mode={import.meta.env.VITE_APP_MODE === "dev" ? "dev" : "prod"}
        onMenuOpen={() => setMobileOpen(true)}
        copilotOpen={copilotOpen}
        onToggleCopilot={() => setCopilotOpen((v) => !v)}
      />

      <div className="flex min-h-[calc(100vh-64px)]">
        <RepNav mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <RepContextHeader
            pageTitle={pageTitle}
            periods={periods}
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
          />

          <Routes>
            {/* S805: Default switched to new-arch my-comp — Decision 5 verification complete */}
            <Route index element={<Navigate to="my-comp" replace />} />

            {/* OLD-ARCH ROUTES — statement and disputes remain on old-arch endpoints (no new spec) */}
            {/* My Payout old-arch preserved but removed from nav — superseded by payout-detail */}
            <Route path="payout"    element={<MyPayout period={selectedPeriod} />} />
            <Route path="statement" element={<MyStatement period={selectedPeriod} batches={batches} batchesLoading={batchesLoading} batchesError={batchesError} periods={periods} />} />
            <Route path="disputes"  element={<Disputes period={selectedPeriod} repPositionId={repPositionId} />} />

            {/* NEW-ARCH ROUTES — S805: unified primary experience */}
            <Route path="my-comp" element={<MyCompensationPage period={selectedPeriod} repPositionId={repPositionId} payoutSummaryId={repPayoutSummaryId} positionLoading={positionLoading} userName={user?.full_name ?? null} />} />
            <Route path="payout-detail" element={<RepPayoutExplanationPage period={selectedPeriod} repPositionId={repPositionId} payoutSummaryId={repPayoutSummaryId} positionLoading={positionLoading} userName={user?.full_name ?? null} />} />
            <Route path="history" element={<CompensationHistoryPage repPositionId={repPositionId} positionLoading={positionLoading} />} />

            <Route path="*" element={<Navigate to="my-comp" replace />} />
          </Routes>

        </main>

        {/* Copilot — shared CopilotPanel, S241 wiring. Sibling to main, not child. */}
        <CopilotPanel
          open={copilotOpen}
          fiscalPeriodId={selectedPeriod?.fiscal_period_id ?? null}
          stageId={null}
          stageName={null}
          aiQueryEnabled={aiQueryEnabled}
        />
      </div>
    </div>
  );
}
